import {
    FormEventHandler,
    ReactElement,
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState
} from 'react';
import { useUpdatePasswordAction } from '../actions';
import { ChangeEmailSectionFormRef } from './change-email-section';
import {
    UIFormInput,
    UIFormInputRef,
    UISection,
    UISectionContent,
    UISectionFooter,
    UISectionHeader,
    UIText,
    UIToast
} from '@/ui';
import { validatePassword } from '@/common/util';
import { useMeTranslation } from '../hooks';

export function ChangePasswordSection(): ReactElement {
    const formRef = useRef<ChangeEmailSectionFormRef>(null);

    const updatePassword = useUpdatePasswordAction();

    function onSubmit(data: { newPassword: string; currentPassword: string }) {
        const { newPassword, currentPassword } = data;
        updatePassword
            .updatePassword({ newPassword, oldPassword: currentPassword })
            .then(() => {
                formRef.current?.resetForm();
            })
            .catch(() => {});
    }

    return (
        <ChangePasswordSectionForm
            ref={formRef}
            loading={updatePassword.loading}
            onSubmit={onSubmit}
        />
    );
}

type ChangePasswordSectionFormProps = {
    loading: boolean;
    onSubmit: (data: { newPassword: string; currentPassword: string }) => void;
};

export type ChangePasswordSectionFormRef = {
    resetForm: () => void;
};

export const ChangePasswordSectionForm = forwardRef<
    ChangePasswordSectionFormRef,
    ChangePasswordSectionFormProps
>((props, ref): ReactElement => {
    const { loading, onSubmit } = props;

    const { t } = useMeTranslation();

    const [showRealTimeErrors, setShowRealTimeErrors] = useState(false);

    /**
     * Current Password
     */
    const currentPasswordRef = useRef<UIFormInputRef>(null);
    const [currentPassword, setCurrentPassword] = useState('');
    const [currentPasswordError, setCurrentPasswordError] = useState('');
    const checkCurrentPassword = useCallback(
        (password: string): string => {
            if (password.length === 0) {
                return t(
                    'components.change_password_section.current_password_error_required'
                );
            }

            return '';
        },
        [t]
    );
    useEffect(() => {
        if (showRealTimeErrors) {
            setCurrentPasswordError(checkCurrentPassword(currentPassword));
        }
    }, [currentPassword, showRealTimeErrors, checkCurrentPassword]);

    /**
     * New Password
     */
    const newPasswordRef = useRef<UIFormInputRef>(null);
    const [newPassword, setNewPassword] = useState('');
    const [newPasswordError, setNewPasswordError] = useState('');
    const checkNewPassword = useCallback(
        async (password: string): Promise<string> => {
            const error = await validatePassword(password);
            if (error) {
                return t(error);
            }
            return '';
        },
        [t]
    );
    useEffect(() => {
        let isMounted = true;
        if (showRealTimeErrors) {
            checkNewPassword(newPassword)
                .then((response) => {
                    if (isMounted) {
                        setNewPasswordError(response);
                    }
                })
                .catch(() => {
                    if (isMounted) {
                        setNewPasswordError(t('shared.errors.generic'));
                    }
                });
        }

        return () => {
            isMounted = false; // Cleanup function
        };
    }, [newPassword, showRealTimeErrors, checkNewPassword, t]);

    /**
     * Confirm New Password
     */
    const confirmNewPasswordRef = useRef<UIFormInputRef>(null);
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [confirmNewPasswordError, setConfirmNewPasswordError] = useState('');
    const checkConfirmNewPassword = useCallback(
        (confirmEmail: string): string => {
            const trimmed = confirmEmail.trim().toLowerCase();
            const emailTrimmed = newPassword.trim().toLowerCase();
            if (trimmed !== emailTrimmed) {
                return t(
                    'components.change_password_section.confirm_password_error_mismatch'
                );
            }

            return '';
        },
        [newPassword, t]
    );
    useEffect(() => {
        if (showRealTimeErrors) {
            setConfirmNewPasswordError(
                checkConfirmNewPassword(confirmNewPassword)
            );
        }
    }, [confirmNewPassword, showRealTimeErrors, checkConfirmNewPassword]);

    const haveValuesChanged = useMemo(() => {
        return newPassword !== '' && confirmNewPassword !== '';
    }, [newPassword, confirmNewPassword]);

    function resetForm() {
        setCurrentPassword('');
        setCurrentPasswordError('');
        setNewPassword('');
        setNewPasswordError('');
        setConfirmNewPassword('');
        setConfirmNewPasswordError('');
        setShowRealTimeErrors(false);
    }

    useImperativeHandle(ref, () => ({
        resetForm() {
            resetForm();
        }
    }));

    const handleSubmit: FormEventHandler = (e) => {
        e.preventDefault();

        setShowRealTimeErrors(true);

        checkNewPassword(newPassword)
            .then((isPasswordValid) => {
                if (
                    !checkCurrentPassword(currentPassword) &&
                    !isPasswordValid &&
                    !checkConfirmNewPassword(confirmNewPassword)
                ) {
                    setShowRealTimeErrors(false);
                    onSubmit?.({ newPassword, currentPassword });
                }
            })
            .catch(() => {
                UIToast.error(t('shared.errors.generic'));
            });
    };

    return (
        <UISection>
            <form onSubmit={handleSubmit} noValidate>
                <UISectionHeader
                    title={t('components.change_password_section.title')}
                />
                <UISectionContent>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 24
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: 16
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flex: 1,
                                    flexDirection: 'column',
                                    gap: 4
                                }}
                            >
                                <UIText>
                                    {t(
                                        'components.change_password_section.current_password_label'
                                    )}
                                </UIText>
                                <UIText type="default2">
                                    {t(
                                        'components.change_password_section.current_password_description'
                                    )}
                                </UIText>
                            </div>

                            <div
                                style={{
                                    display: 'flex',
                                    flex: 1
                                }}
                            >
                                <UIFormInput
                                    ref={currentPasswordRef}
                                    name="Current Password"
                                    value={currentPassword}
                                    placeholder={t(
                                        'components.change_password_section.current_password_placeholder'
                                    )}
                                    type="password"
                                    errorText={currentPasswordError}
                                    required
                                    maxLength={100}
                                    onChange={setCurrentPassword}
                                />
                            </div>
                        </div>
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: 16
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flex: 1,
                                    flexDirection: 'column',
                                    gap: 4
                                }}
                            >
                                <UIText>
                                    {t(
                                        'components.change_password_section.new_password_label'
                                    )}
                                </UIText>
                            </div>

                            <div
                                style={{
                                    display: 'flex',
                                    flex: 1
                                }}
                            >
                                <UIFormInput
                                    ref={newPasswordRef}
                                    name="New Password"
                                    value={newPassword}
                                    placeholder={t(
                                        'components.change_password_section.new_password_placeholder'
                                    )}
                                    type="password"
                                    autoComplete="on"
                                    errorText={newPasswordError}
                                    required
                                    maxLength={100}
                                    onChange={setNewPassword}
                                />
                            </div>
                        </div>
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: 16
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flex: 1,
                                    flexDirection: 'column',
                                    gap: 4
                                }}
                            >
                                <UIText>
                                    {t(
                                        'components.change_password_section.confirm_password_label'
                                    )}
                                </UIText>
                                <UIText type="default2">
                                    {t(
                                        'components.change_password_section.confirm_password_description'
                                    )}
                                </UIText>
                            </div>

                            <div
                                style={{
                                    display: 'flex',
                                    flex: 1
                                }}
                            >
                                <UIFormInput
                                    ref={confirmNewPasswordRef}
                                    name="Confirm New Password"
                                    value={confirmNewPassword}
                                    placeholder={t(
                                        'components.change_password_section.confirm_password_placeholder'
                                    )}
                                    type="password"
                                    autoComplete="on"
                                    errorText={confirmNewPasswordError}
                                    required
                                    maxLength={100}
                                    onChange={setConfirmNewPassword}
                                />
                            </div>
                        </div>
                    </div>
                </UISectionContent>
                {haveValuesChanged && (
                    <UISectionFooter
                        actions={[
                            {
                                variant: 'primary',
                                type: 'submit',
                                loading: loading,
                                disabled: loading,
                                text: loading
                                    ? t('shared.labels.saving')
                                    : t('shared.labels.save')
                            },
                            {
                                text: t('shared.labels.cancel'),
                                disabled: loading,
                                onClick: resetForm
                            }
                        ]}
                    />
                )}
            </form>
        </UISection>
    );
});
